import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Eye, RefreshCw, Download, Search, FileText, ExternalLink, Euro, Tag, Globe, MessageSquare, Calendar } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const Veille = () => {
  const { token } = useAuth();
  const [concurrents, setConcurrents] = useState([]);
  const [posts, setPosts] = useState([]);
  const [rapport, setRapport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scraping, setScraping] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [postDialogOpen, setPostDialogOpen] = useState(false);
  const [activeView, setActiveView] = useState("timeline"); // timeline, rapport, concurrents
  const [filterConcurrent, setFilterConcurrent] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  const [concForm, setConcForm] = useState({ nom: "", url: "", type_source: "page" });
  const [postForm, setPostForm] = useState({ concurrent_id: "", texte: "", date_post: new Date().toISOString().slice(0, 10), url_post: "" });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchAll = async () => {
    try {
      const [concRes, postsRes] = await Promise.all([
        axios.get(`${API}/veille/concurrents`, { headers }),
        axios.get(`${API}/veille/posts?limit=100`, { headers }),
      ]);
      setConcurrents(concRes.data);
      setPosts(postsRes.data);
    } catch { toast.error("Erreur chargement données veille"); }
    finally { setLoading(false); }
  };

  const fetchRapport = async () => {
    try {
      const res = await axios.get(`${API}/veille/rapport-hebdo`, { headers });
      setRapport(res.data);
    } catch { toast.error("Erreur chargement rapport"); }
  };

  useEffect(() => { fetchAll(); fetchRapport(); }, []);

  const handleAddConcurrent = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/veille/concurrents`, concForm, { headers });
      toast.success("Concurrent ajouté !");
      setAddDialogOpen(false);
      setConcForm({ nom: "", url: "", type_source: "page" });
      fetchAll();
    } catch { toast.error("Erreur ajout"); }
  };

  const handleDeleteConcurrent = async (id) => {
    if (!window.confirm("Supprimer ce concurrent et tous ses posts ?")) return;
    try {
      await axios.delete(`${API}/veille/concurrents/${id}`, { headers });
      toast.success("Concurrent supprimé");
      fetchAll();
    } catch { toast.error("Erreur"); }
  };

  const handleScrapeAll = async () => {
    setScraping(true);
    try {
      const res = await axios.post(`${API}/veille/scrape`, {}, { headers });
      toast.success(res.data.message);
      if (res.data.resultats) {
        res.data.resultats.forEach(r => {
          if (r.statut === "ok") toast.info(`${r.concurrent}: ${r.nouveaux} nouveaux posts`);
          else if (r.statut === "erreur") toast.error(`${r.concurrent}: erreur de scraping`);
        });
      }
      fetchAll();
      fetchRapport();
    } catch { toast.error("Erreur lors du scraping"); }
    finally { setScraping(false); }
  };

  const handleScrapeOne = async (id) => {
    setScraping(true);
    try {
      const res = await axios.post(`${API}/veille/scrape/${id}`, {}, { headers });
      toast.success(`${res.data.concurrent}: ${res.data.nouveaux} nouveaux posts`);
      fetchAll();
    } catch { toast.error("Erreur scraping"); }
    finally { setScraping(false); }
  };

  const handleAddPost = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/veille/posts`, postForm, { headers });
      toast.success("Post ajouté manuellement");
      setPostDialogOpen(false);
      setPostForm({ concurrent_id: "", texte: "", date_post: new Date().toISOString().slice(0, 10), url_post: "" });
      fetchAll();
    } catch { toast.error("Erreur"); }
  };

  const handleDeletePost = async (id) => {
    try {
      await axios.delete(`${API}/veille/posts/${id}`, { headers });
      toast.success("Post supprimé");
      fetchAll();
    } catch { toast.error("Erreur"); }
  };

  const handleDownloadPDF = async () => {
    try {
      const res = await axios.get(`${API}/veille/rapport-hebdo/pdf`, { headers, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = "veille_hebdo.pdf"; a.click(); a.remove();
      toast.success("PDF téléchargé !");
    } catch { toast.error("Erreur téléchargement PDF"); }
  };

  const filteredPosts = posts.filter(p => {
    if (filterConcurrent !== "all" && p.concurrent_id !== filterConcurrent) return false;
    if (searchTerm && !p.texte?.toLowerCase().includes(searchTerm.toLowerCase()) && !p.concurrent_nom?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  const allPrix = posts.flatMap(p => p.prix_detectes || []);
  const prixMoyen = allPrix.length > 0 ? (allPrix.reduce((a, b) => a + b, 0) / allPrix.length) : 0;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="veille-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Veille Concurrentielle</h1>
          <p className="text-slate-500 mt-1">Surveillance des pages Facebook concurrentes</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => setPostDialogOpen(true)} data-testid="add-post-btn">
            <Plus className="w-4 h-4 mr-2" /> Post manuel
          </Button>
          <Button variant="outline" onClick={handleScrapeAll} disabled={scraping || concurrents.length === 0} data-testid="scrape-all-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${scraping ? "animate-spin" : ""}`} /> {scraping ? "Scraping..." : "Scanner maintenant"}
          </Button>
          <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={() => setAddDialogOpen(true)} data-testid="add-concurrent-btn">
            <Plus className="w-4 h-4 mr-2" /> Concurrent
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card><CardContent className="p-4"><p className="text-sm text-slate-500">Concurrents suivis</p><p className="text-2xl font-bold text-slate-800" data-testid="count-concurrents">{concurrents.length}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-sm text-slate-500">Posts collectés</p><p className="text-2xl font-bold text-indigo-600">{posts.length}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-sm text-slate-500">Prix moyen détecté</p><p className="text-2xl font-bold text-emerald-600">{prixMoyen > 0 ? `${prixMoyen.toFixed(0)} €` : "—"}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-sm text-slate-500">Cette semaine</p><p className="text-2xl font-bold text-amber-600">{rapport?.total_posts || 0} posts</p></CardContent></Card>
      </div>

      {/* Tabs */}
      <div className="flex gap-2">
        {[
          { key: "timeline", label: "Timeline", icon: MessageSquare },
          { key: "rapport", label: "Rapport Hebdo", icon: FileText },
          { key: "concurrents", label: "Concurrents", icon: Globe },
        ].map(({ key, label, icon: Icon }) => (
          <Button key={key} variant={activeView === key ? "default" : "outline"}
            className={activeView === key ? "bg-indigo-600" : ""}
            onClick={() => { setActiveView(key); if (key === "rapport") fetchRapport(); }}
            data-testid={`tab-${key}`}>
            <Icon className="w-4 h-4 mr-2" />{label}
          </Button>
        ))}
      </div>

      {/* TIMELINE VIEW */}
      {activeView === "timeline" && (
        <>
          <Card>
            <CardContent className="p-4 flex gap-4 flex-wrap items-end">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input placeholder="Rechercher dans les posts..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10" data-testid="search-posts" />
                </div>
              </div>
              <div>
                <Select value={filterConcurrent} onValueChange={setFilterConcurrent}>
                  <SelectTrigger className="w-48" data-testid="filter-concurrent"><SelectValue placeholder="Filtrer..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les concurrents</SelectItem>
                    {concurrents.map(c => <SelectItem key={c.id} value={c.id}>{c.nom}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-3">
            {loading ? (
              <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div></div>
            ) : filteredPosts.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center text-slate-500">
                  <Eye className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p className="font-medium">Aucun post trouvé</p>
                  <p className="text-sm mt-1">Ajoutez des concurrents et lancez un scan, ou ajoutez des posts manuellement</p>
                </CardContent>
              </Card>
            ) : (
              filteredPosts.map(post => (
                <Card key={post.id} className="hover:shadow-md transition-shadow" data-testid={`post-${post.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="outline" className="text-xs shrink-0">{post.concurrent_nom || "—"}</Badge>
                          <span className="text-xs text-slate-400">{post.date_post || post.date_scrape?.slice(0, 10)}</span>
                          {post.source_type === "manuel" && <Badge className="bg-amber-100 text-amber-700 text-xs">Manuel</Badge>}
                        </div>
                        <p className="text-sm text-slate-700 leading-relaxed">{post.texte}</p>
                        <div className="flex items-center gap-3 mt-2">
                          {(post.prix_detectes || []).length > 0 && (
                            <div className="flex items-center gap-1">
                              <Euro className="w-3 h-3 text-red-500" />
                              {post.prix_detectes.map((p, i) => (
                                <Badge key={i} className="bg-red-50 text-red-700 text-xs">{p.toFixed(0)} €</Badge>
                              ))}
                            </div>
                          )}
                          {post.url_post && (
                            <a href={post.url_post} target="_blank" rel="noopener noreferrer" className="text-xs text-indigo-500 hover:underline flex items-center gap-1">
                              <ExternalLink className="w-3 h-3" />Voir sur Facebook
                            </a>
                          )}
                        </div>
                      </div>
                      <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-600 shrink-0" onClick={() => handleDeletePost(post.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </>
      )}

      {/* RAPPORT HEBDO VIEW */}
      {activeView === "rapport" && (
        <>
          <div className="flex justify-end">
            <Button onClick={handleDownloadPDF} className="bg-indigo-600 hover:bg-indigo-700" data-testid="download-rapport-pdf">
              <Download className="w-4 h-4 mr-2" /> Télécharger PDF
            </Button>
          </div>
          {rapport ? (
            <div className="space-y-4">
              <Card>
                <CardHeader><CardTitle className="text-lg">Résumé de la semaine ({rapport.periode?.debut} — {rapport.periode?.fin})</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="p-3 bg-slate-50 rounded-lg"><p className="text-sm text-slate-500">Total posts</p><p className="text-xl font-bold">{rapport.total_posts}</p></div>
                    <div className="p-3 bg-slate-50 rounded-lg"><p className="text-sm text-slate-500">Concurrents actifs</p><p className="text-xl font-bold">{rapport.total_concurrents_actifs}</p></div>
                    <div className="p-3 bg-slate-50 rounded-lg"><p className="text-sm text-slate-500">Prix moyen</p><p className="text-xl font-bold">{rapport.prix_moyen_detecte ? `${rapport.prix_moyen_detecte.toFixed(0)} €` : "—"}</p></div>
                  </div>
                </CardContent>
              </Card>
              {(rapport.par_concurrent || []).map((conc, i) => (
                <Card key={i}>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Globe className="w-4 h-4 text-indigo-600" />{conc.nom}
                      <Badge variant="secondary">{conc.posts.length} posts</Badge>
                      {conc.prix_min != null && (
                        <Badge className="bg-red-50 text-red-700">
                          {conc.prix_min === conc.prix_max ? `${conc.prix_min.toFixed(0)}€` : `${conc.prix_min.toFixed(0)}€ - ${conc.prix_max.toFixed(0)}€`}
                        </Badge>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {conc.posts.slice(0, 5).map((p, j) => (
                        <div key={j} className="p-2 bg-slate-50 rounded text-sm">
                          <span className="text-slate-400 text-xs mr-2">{p.date_post?.slice(0, 10)}</span>
                          {p.texte?.slice(0, 200)}{p.texte?.length > 200 ? "..." : ""}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
              {rapport.total_posts === 0 && (
                <Card><CardContent className="p-12 text-center text-slate-500"><Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" /><p>Aucune activité cette semaine</p></CardContent></Card>
              )}
            </div>
          ) : (
            <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div></div>
          )}
        </>
      )}

      {/* CONCURRENTS VIEW */}
      {activeView === "concurrents" && (
        <Card data-testid="concurrents-table-card">
          <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Globe className="w-5 h-5 text-indigo-600" />Concurrents surveillés ({concurrents.length})</CardTitle></CardHeader>
          <CardContent>
            {concurrents.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Globe className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p className="font-medium">Aucun concurrent</p>
                <p className="text-sm mt-1">Ajoutez des pages Facebook ou Marketplace à surveiller</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nom</TableHead>
                    <TableHead>URL</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Dernier scan</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead className="w-32"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {concurrents.map(c => (
                    <TableRow key={c.id} data-testid={`concurrent-row-${c.id}`}>
                      <TableCell className="font-medium">{c.nom}</TableCell>
                      <TableCell className="max-w-48 truncate">
                        <a href={c.url} target="_blank" rel="noopener noreferrer" className="text-indigo-500 hover:underline text-sm flex items-center gap-1">
                          <ExternalLink className="w-3 h-3 shrink-0" />{c.url.replace(/https?:\/\/(www\.)?/, "").slice(0, 40)}
                        </a>
                      </TableCell>
                      <TableCell><Badge variant="outline">{c.type_source === "marketplace" ? "Marketplace" : "Page"}</Badge></TableCell>
                      <TableCell className="text-sm text-slate-500">{c.dernier_scrape ? new Date(c.dernier_scrape).toLocaleDateString('fr-FR') : "Jamais"}</TableCell>
                      <TableCell>
                        <Badge className={
                          c.dernier_scrape_statut === "ok" ? "bg-emerald-100 text-emerald-700" :
                          c.dernier_scrape_statut === "erreur" ? "bg-red-100 text-red-700" :
                          c.dernier_scrape_statut === "en_cours" ? "bg-blue-100 text-blue-700" :
                          "bg-slate-100 text-slate-500"
                        }>
                          {c.dernier_scrape_statut || "En attente"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => handleScrapeOne(c.id)} disabled={scraping} data-testid={`scrape-${c.id}`}>
                            <RefreshCw className={`w-4 h-4 ${scraping ? "animate-spin" : ""}`} />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDeleteConcurrent(c.id)} data-testid={`delete-concurrent-${c.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* ADD CONCURRENT DIALOG */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent data-testid="add-concurrent-dialog">
          <DialogHeader><DialogTitle>Ajouter un concurrent</DialogTitle></DialogHeader>
          <form onSubmit={handleAddConcurrent} className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label>Nom du concurrent</Label>
              <Input value={concForm.nom} onChange={e => setConcForm({ ...concForm, nom: e.target.value })} placeholder="Ex: MeublePlus" required data-testid="concurrent-nom-input" />
            </div>
            <div className="space-y-2">
              <Label>URL Facebook / Marketplace</Label>
              <Input value={concForm.url} onChange={e => setConcForm({ ...concForm, url: e.target.value })} placeholder="https://facebook.com/meubleplus" required data-testid="concurrent-url-input" />
            </div>
            <div className="space-y-2">
              <Label>Type de source</Label>
              <Select value={concForm.type_source} onValueChange={v => setConcForm({ ...concForm, type_source: v })}>
                <SelectTrigger data-testid="concurrent-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="page">Page Facebook</SelectItem>
                  <SelectItem value="marketplace">Marketplace</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="submit-concurrent-btn">Ajouter</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* ADD POST MANUAL DIALOG */}
      <Dialog open={postDialogOpen} onOpenChange={setPostDialogOpen}>
        <DialogContent data-testid="add-post-dialog">
          <DialogHeader><DialogTitle>Ajouter un post manuellement</DialogTitle></DialogHeader>
          <form onSubmit={handleAddPost} className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label>Concurrent</Label>
              <Select value={postForm.concurrent_id} onValueChange={v => setPostForm({ ...postForm, concurrent_id: v })}>
                <SelectTrigger data-testid="post-concurrent-select"><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
                <SelectContent>
                  {concurrents.map(c => <SelectItem key={c.id} value={c.id}>{c.nom}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Texte du post / description</Label>
              <textarea value={postForm.texte} onChange={e => setPostForm({ ...postForm, texte: e.target.value })}
                className="w-full min-h-[100px] p-3 border rounded-md text-sm" placeholder="Copiez-collez le texte du post ici..." required data-testid="post-texte-input" />
              <p className="text-xs text-slate-500">Les prix seront détectés automatiquement dans le texte</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Date du post</Label><Input type="date" value={postForm.date_post} onChange={e => setPostForm({ ...postForm, date_post: e.target.value })} data-testid="post-date-input" /></div>
              <div className="space-y-2"><Label>Lien (optionnel)</Label><Input value={postForm.url_post} onChange={e => setPostForm({ ...postForm, url_post: e.target.value })} placeholder="https://..." /></div>
            </div>
            <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="submit-post-btn">Ajouter le post</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Veille;
